/**
 * Route definition for /collections/$networkSlug/$collectionSlug
 *
 * Uses TanStack Router's loader to prefetch collection data server-side.
 * Fetches collection information and the first page of NFTs before render.
 */

import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'react-toastify'
import { getCollectionInfo, searchNFTs } from '@/lib/api'
import CollectionContainer from '@/containers/Collection'
import chainData from '@/chains'

export const Route = createFileRoute('/$locale/collections/$networkSlug/$collectionSlug')({
  staleTime: Number.MAX_SAFE_INTEGER,
  loader: async ({ params: { networkSlug, collectionSlug } }) => {
    // TanStack Router re-runs loaders on initial client hydration.
    // Re-use SSR loader data if present.
    if (typeof window !== 'undefined' && (window as any).__SSR_COLLECTION_DETAIL__) {
      const cached = (window as any).__SSR_COLLECTION_DETAIL__
      delete (window as any).__SSR_COLLECTION_DETAIL__
      if (cached.collectionId !== undefined) {
        return cached
      }
    }

    if (!collectionSlug) {
      throw new Error('Collection slug is required')
    }

    const isNumeric = /^\d+$/.test(collectionSlug)
    const isEthAddress = /^0x[a-fA-F0-9]{40}$/.test(collectionSlug)

    try {
      let collectionInfo: any
      let searchResult: any

      const nftOptions = { offset: 0, limit: 40, order: 'listings' as const }

      if (isNumeric) {
        const collId = Number(collectionSlug)
        ;[collectionInfo, searchResult] = await Promise.all([
          getCollectionInfo({ collId }),
          searchNFTs(collId, nftOptions),
        ])
      } else if (isEthAddress) {
        ;[collectionInfo, searchResult] = await Promise.all([
          getCollectionInfo({ contractAddress: collectionSlug, chainName: networkSlug }),
          searchNFTs(collectionSlug, { ...nftOptions, canonicalName: collectionSlug, chainName: networkSlug }),
        ])
      } else {
        ;[collectionInfo, searchResult] = await Promise.all([
          getCollectionInfo({ canonicalName: collectionSlug, chainName: networkSlug }),
          searchNFTs(collectionSlug, { ...nftOptions, canonicalName: collectionSlug, chainName: networkSlug }),
        ])
      }

      const initialNfts = searchResult?.items || []

      const collId = collectionInfo?.id
      if (collId === undefined || collId === null) {
        throw new Error(`Collection resolved but numeric id is missing for "${collectionSlug}"`)
      }

      return {
        initialCollectionInfo: collectionInfo,
        initialNfts,
        collectionSlug,
        networkSlug,
        collectionId: collId,
      }
    } catch (error) {
      console.error('Failed to load collection:', error)
      toast.error('Failed to load collection. Please try again.')
      throw error
    }
  },

  component: CollectionPage,
})

function CollectionPage() {
  const { initialCollectionInfo, initialNfts, collectionSlug, networkSlug, collectionId } = Route.useLoaderData() as {
    initialCollectionInfo: any
    initialNfts: any
    collectionSlug: string
    networkSlug: string
    collectionId: string | number
  }

  const collectionName = initialCollectionInfo?.title || initialCollectionInfo?.name || collectionSlug
  const chainEntry = Array.isArray(chainData)
    ? chainData.find((entry: any) => entry && String(entry.chainId) === String(initialCollectionInfo?.chain))
    : null
  const chainId = chainEntry?.chainId

  return (
    <CollectionContainer
      collectionId={String(collectionId)}
      initialCollectionInfo={initialCollectionInfo}
      initialNfts={initialNfts}
      initialVolData={initialCollectionInfo}
      collectionName={collectionName}
      logo={chainId ? `/assets/img/chain-logos/chain-${chainId}.png` : undefined}
    />
  )
}
